import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useT, useLang } from '../i18n';
import { useIsAdmin } from '../AuthContext';
import { tx as __tx } from "../lib/tx";

const PRODUCTS = [
  { key: 'xp', label: 'Expense', kbd: '⌘1', dot: '#00e5a0', href: 'https://app.xpnsr.tech' },
  { key: 'seo', label: 'CBN', kbd: '⌘2', dot: '#0076ff', href: null },
  { key: 'trk', label: 'Tracker', kbd: '⌘3', dot: '#f97316', href: 'https://tracker.xpnsr.tech' },
];

export const Sidebar = ({ onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const t = useT();
  const { lang, switchLang } = useLang();
  const isAdmin = useIsAdmin();

  // Cmd/Ctrl + 1/2/3 → switch product (skip while typing)
  useEffect(() => {
    const handler = (e) => {
      if (!e.metaKey && !e.ctrlKey) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const map = { 1: 'xp', 2: 'seo', 3: 'trk' };
      const key = map[e.key];
      if (!key) return;
      const target = PRODUCTS.find(p => p.key === key);
      if (target?.href) {
        e.preventDefault();
        window.location.href = target.href;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close mobile drawer on Esc; lock body scroll while it's open
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Auto-close drawer when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/',           label: t.nav.dashboard },
    { path: '/sites',      label: t.nav.sites },
    { path: '/pbn',        label: t.nav.pbn },
    { path: '/audit',      label: t.nav.audit },
    { path: '/articles',   label: t.nav.articles },
    { path: '/promotion',  label: t.nav.promotion },
    { path: '/automation', label: t.nav.automation },
    { path: '/keywords',   label: t.nav.keywords },
    { path: '/ranks',      label: t.nav.ranks },
    { path: '/reports',    label: t.nav.reports },
    { path: '/settings',   label: t.nav.settings || __tx('Settings') },
    ...(isAdmin ? [{ path: '/admin', label: t.nav.admin || __tx('Admin') }] : []),
  ];

  // Domains section (former xpnsr-seo-addon) - see src/domains/
  const domainNavItems = [
    { path: '/dm',           label: t.nav.dmDomains   || __tx('Домены') },
    { path: '/dm/wizard',    label: 'Wizard' },
    { path: '/dm/accounts',  label: 'Cloudflare' },
    { path: '/dm/spaceship', label: 'Spaceship' },
    { path: '/dm/dns',       label: 'DNS' },
    { path: '/dm/pages',     label: 'Pages' },
    { path: '/dm/workers',   label: 'Workers' },
    { path: '/dm/pbn',       label: t.nav.dmPublisher || __tx('PBN Publisher') },
    { path: '/dm/ftp',       label: 'FTP' },
    { path: '/dm/settings',  label: t.nav.dmSettings  || __tx('Настройки доменов') },
  ];

  return (
    <>
      <button
        data-testid="mobile-menu-btn"
        className="mobile-menu-btn lg:hidden"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? t.nav.closeMenu || __tx('Close menu') : t.nav.openMenu || __tx('Open menu')}
        aria-expanded={isOpen}
        aria-controls="primary-sidebar"
      >
        {isOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true" />
      )}

      <aside
        id="primary-sidebar"
        className={`xp-sidebar ${isOpen ? 'open' : ''}`}
        aria-label={t.nav.primaryNavigation || __tx('Primary navigation')}
        aria-modal={isOpen ? true : undefined}
      >
        {/* Brand */}
        <div className="xp-brand">
          <span className="xp-brand-dot" />
          <span className="xp-brand-logo">xpnsr.</span>
        </div>

        {/* Product switcher */}
        <div className="xp-switcher">
          <div className="xp-section-label">{t.nav.product}</div>
          <div className="xp-sw">
            {PRODUCTS.map((p) => {
              const isActive = p.key === 'seo';
              const className = `xp-sw-btn ${isActive ? 'on' : ''}`;
              const inner = (
                <>
                  <span className="xp-sw-ind" style={{ background: p.dot }} />
                  <span>{p.label}</span>
                  <span className="xp-sw-kbd">{p.kbd}</span>
                </>
              );
              return p.href ? (
                <a
                  key={p.key}
                  className={className}
                  href={p.href}
                  data-p={p.key}
                  data-testid={`product-${p.key}`}
                >
                  {inner}
                </a>
              ) : (
                <button
                  key={p.key}
                  type="button"
                  className={className}
                  data-p={p.key}
                  data-testid={`product-${p.key}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {inner}
                </button>
              );
            })}
          </div>
        </div>

        {/* Nav */}
        <nav className="xp-nav-group" aria-label={t.nav.primaryNavigation || __tx('Primary navigation')}>
          <h6>{t.nav.navigate}</h6>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                className={({ isActive: navActive }) => `xp-nav-link ${navActive || isActive ? 'on' : ''}`}
                onClick={() => setIsOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                end={item.path === '/'}
              >
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <nav className="xp-nav-group" aria-label={__tx("Domains")}>
          <h6>{t.nav.dmGroup || __tx('Домены')}</h6>
          {domainNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dm'}
              data-testid={'nav-dm-' + (item.path.replace('/dm', '').replace('/', '') || 'domains')}
              className={({ isActive }) => 'xp-nav-link ' + (isActive ? 'on' : '')}
              onClick={() => setIsOpen(false)}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer: lang + logout (theme toggle now lives in TopBar) */}
        <div className="xp-sidebar-footer">
          <div
            className="xp-lang"
            role="group"
            aria-label={t.nav.languageGroup || __tx('Language')}
          >
            {['en', 'ru'].map(l => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                className={`xp-lang-btn ${lang === l ? 'on' : ''}`}
                aria-pressed={lang === l}
                aria-label={l === 'en' ? 'English' : __tx('Русский')}
              >
                {l === 'en' ? 'EN' : 'RU'}
              </button>
            ))}
          </div>

          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="xp-logout"
              aria-label={t.nav.signOut}
            >
              <LogOut size={12} aria-hidden="true" />
              <span>{t.nav.signOut}</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
